import React, { useMemo } from 'react';
import { X, WarningCircle } from '@phosphor-icons/react';
import MovementBadge from './MovementBadge';

// Client-side preview of court movement, mirroring backend rules:
//   • top-court winner stays
//   • bottom-court loser stays
//   • ties on a court are flagged (not resolved here)
//   • a LOCKED court keeps every player on the same court
//   • a court adjacent to a locked court does NOT exchange with it
//   • SUB slots (is_substitute === true) are excluded from BOTH high-winner
//     and low-loser selection. Score is shown for history, but the absent
//     player neither moves up nor down based on the sub's play.
export function computeMovementPreview(courts, numCourts, resolved = []) {
  const lockedSet = new Set(
    courts.filter((c) => c.movement_locked).map((c) => c.court_number),
  );
  const resolvedMap = {};
  (resolved || []).forEach((t) => {
    if (t && t.registration_id) resolvedMap[`${t.court_number}-${t.role}`] = t.registration_id;
  });
  return courts.map((c) => {
    const sorted = [...c.slots].sort((a, b) => (b.points ?? -1) - (a.points ?? -1));
    if (sorted.length === 0) return { court_number: c.court_number, rows: [], ties: [], movement_locked: lockedSet.has(c.court_number), skipped_subs: [] };

    // Eligible-for-movement = non-sub slots only.
    const eligible = sorted.filter((s) => !s.is_substitute);
    const thisLocked = lockedSet.has(c.court_number);
    const upBlocked = thisLocked || lockedSet.has(c.court_number - 1);
    const downBlocked = thisLocked || lockedSet.has(c.court_number + 1);
    const isTopCourt = c.court_number === 1;
    const isBottomCourt = c.court_number === numCourts;

    // Identify subs whose score >= what would otherwise have been the high
    // winner — these are the "skipped" subs for the informational banner.
    let skippedSubs = [];
    let winnerId = null;
    let loserId = null;

    if (eligible.length === 0) {
      // All subs — no movement at all.
      const rows = sorted.map((s) => ({ ...s, movement: 'stay', nextCourt: c.court_number }));
      return { court_number: c.court_number, rows, ties: [], movement_locked: thisLocked, skipped_subs: sorted.filter((s) => s.is_substitute).map((s) => s.registration_id) };
    }

    const upPossible = !isTopCourt && !upBlocked;
    const downPossible = !isBottomCourt && !downBlocked;
    const ties = [];

    // UP (upward): non-sub only; only when up movement is possible.
    const topPts = eligible[0].points;
    const highs = eligible.filter((s) => s.points === topPts);
    if (upPossible) {
      if (highs.length > 1) {
        const picked = resolvedMap[`${c.court_number}-highest`];
        if (picked && highs.some((s) => s.registration_id === picked)) winnerId = picked;
        else ties.push({ role: 'highest', count: highs.length, points: topPts });
      } else {
        winnerId = highs[0].registration_id;
      }
    }

    // DOWN (downward): INCLUDE subs; exclude the high winner; only when down is possible.
    let botPts = null;
    if (downPossible) {
      botPts = sorted[sorted.length - 1].points;
      const lows = sorted.filter((s) => s.points === botPts && s.registration_id !== winnerId);
      if (lows.length > 1) {
        const picked = resolvedMap[`${c.court_number}-lowest`];
        if (picked && lows.some((s) => s.registration_id === picked)) loserId = picked;
        else ties.push({ role: 'lowest', count: lows.length, points: botPts });
      } else if (lows.length === 1) {
        loserId = lows[0].registration_id;
      }
    }

    skippedSubs = sorted.filter((s) => s.is_substitute && s.points !== null && s.points >= topPts).map((s) => s.registration_id);

    const rows = sorted.map((s) => {
      let movement = 'stay';
      let nextCourt = c.court_number;
      if (winnerId && s.registration_id === winnerId) {
        if (isTopCourt || upBlocked) { movement = 'stay'; nextCourt = c.court_number; }
        else { movement = 'up'; nextCourt = c.court_number - 1; }
      } else if (loserId && s.registration_id === loserId) {
        if (isBottomCourt || downBlocked) { movement = 'stay'; nextCourt = c.court_number; }
        else { movement = 'down'; nextCourt = c.court_number + 1; }
      }
      return { ...s, movement, nextCourt };
    });
    return { court_number: c.court_number, rows, ties, movement_locked: thisLocked, skipped_subs: skippedSubs };
  });
}

export default function FinalizePreviewDialog({
  open,
  weekNumber,
  numCourts,
  courts,
  tieBreakers = [],
  onCancel,
  onConfirm,
  busy,
}) {
  const preview = useMemo(
    () => (open && courts ? computeMovementPreview(courts, numCourts, tieBreakers) : []),
    [open, courts, numCourts, tieBreakers],
  );

  if (!open) return null;

  const hasIncomplete = courts.some((c) => c.slots.some((s) => s.points === null || s.points === undefined));
  const allZeros = courts.every((c) => c.slots.every((s) => (s.points ?? 0) === 0));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onClick={onCancel}
      data-testid="finalize-preview-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl bg-[#fbfaf8] border border-[#e3dcd1] rounded-t-2xl sm:rounded-2xl max-h-modal flex flex-col overflow-hidden"
        data-testid="finalize-preview-dialog"
      >
        {/* Header + banners — fixed top region (never scrolls away) */}
        <div className="flex-shrink-0 p-5 pb-3 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-2xl text-[#013523]">Finalize Week {weekNumber}?</h3>
            <p className="text-xs text-[#6a766e] mt-0.5">Review movement before committing.</p>
          </div>
          <button onClick={onCancel} className="text-[#6a766e]" data-testid="finalize-preview-close">
            <X size={20} />
          </button>
        </div>

        {hasIncomplete && (
          <div data-testid="finalize-incomplete-banner" className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
            <WarningCircle size={16} weight="fill" />
            Enter points for all players before finalizing.
          </div>
        )}
        {(() => {
          const lockedCourts = preview.filter((p) => p.movement_locked).map((p) => p.court_number);
          if (lockedCourts.length === 0) return null;
          const list = lockedCourts.length === 1
            ? `Court ${lockedCourts[0]}`
            : `Courts ${lockedCourts.slice(0, -1).join(', ')} and ${lockedCourts[lockedCourts.length - 1]}`;
          return (
            <div
              data-testid="finalize-lock-banner"
              className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex gap-2"
            >
              <WarningCircle size={16} weight="fill" />
              <div>
                <div className="font-medium uppercase tracking-[0.16em] text-[10px] text-amber-700 mb-0.5">Movement Lock Active</div>
                {list} movement {lockedCourts.length === 1 ? 'is' : 'are'} locked. No players will move into or out of {list} next week.
              </div>
            </div>
          );
        })()}
        {(() => {
          const courtsWithSkippedSub = preview.filter((p) => (p.skipped_subs || []).length > 0).map((p) => p.court_number);
          if (courtsWithSkippedSub.length === 0) return null;
          const list = courtsWithSkippedSub.length === 1
            ? `Court ${courtsWithSkippedSub[0]}`
            : `Courts ${courtsWithSkippedSub.slice(0, -1).join(', ')} and ${courtsWithSkippedSub[courtsWithSkippedSub.length - 1]}`;
          return (
            <div
              data-testid="finalize-sub-skipped-banner"
              className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex gap-2"
            >
              <WarningCircle size={16} weight="fill" />
              <div>
                <div className="font-medium uppercase tracking-[0.16em] text-[10px] text-blue-700 mb-0.5">Sub Score Skipped for Movement</div>
                Sub score recorded on {list}, but subs are not eligible to move a player up. The next eligible player moves up.
              </div>
            </div>
          );
        })()}
        {!hasIncomplete && allZeros && (
          <div data-testid="finalize-zeros-banner" className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
            <WarningCircle size={16} weight="fill" />
            All scores are 0 — confirm before finalizing.
          </div>
        )}

        </div>

        {/* Court movement preview — scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-3">
          {preview.map((p) => (
            <div key={p.court_number} data-testid={`finalize-court-${p.court_number}`} className="border border-[#e3dcd1] rounded-xl p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
                  Court {p.court_number}
                  {p.court_number === 1 && <span className="ml-1 text-[#015e3f]">(top)</span>}
                  {p.court_number === numCourts && <span className="ml-1 text-[#6a766e]">(bottom)</span>}
                </div>
                {p.ties.length > 0 && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    {p.ties.map((t) => t.role).join(' + ')} tie
                  </span>
                )}
              </div>
              <ul className="space-y-1">
                {p.rows.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0 flex items-center gap-2">
                      <MovementBadge movement={r.movement} />
                      <span className="truncate text-[#013523]">
                        {r.no_show ? (
                          <span className="line-through text-[#6a766e]">{r.player_name}</span>
                        ) : r.player_name}
                      </span>
                      {r.is_substitute && r.sub_player_name && (
                        <span className="text-[10px] text-[#7a611a]">(sub: {r.sub_player_name})</span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-medium text-[#013523]">{r.points ?? '—'}</span>
                      <span className="text-[10px] text-[#6a766e] ml-2">
                        {r.movement === 'up' && `→ Court ${r.nextCourt}`}
                        {r.movement === 'down' && `→ Court ${r.nextCourt}`}
                        {r.movement === 'stay' && '→ stays'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex-shrink-0 flex justify-end gap-2 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-[#e3dcd1] bg-[#fbfaf8]">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-[#e3dcd1] text-[#013523] text-sm"
            data-testid="finalize-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={hasIncomplete || busy}
            className="px-4 py-2 rounded-lg bg-[#015e3f] text-white text-sm font-medium disabled:opacity-50"
            data-testid="finalize-confirm-btn"
          >
            {busy ? 'Finalizing…' : `Finalize Week ${weekNumber}`}
          </button>
        </div>
      </div>
    </div>
  );
}
